import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  BellRing,
  Check,
  Circle,
  ExternalLink,
  Info,
  Loader2,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface OosAlertSettingsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const COOLDOWN_OPTIONS = [
  { value: '1', label: '1 hour' },
  { value: '2', label: '2 hours' },
  { value: '4', label: '4 hours' },
  { value: '8', label: '8 hours' },
  { value: '24', label: '24 hours' },
];

export function OosAlertSettingsSheet({ open, onOpenChange }: OosAlertSettingsSheetProps) {
  const [alertMode, setAlertMode] = useState<'key_items' | 'all_items' | 'threshold'>('key_items');

  // --- Queries ---
  const settingsQuery = trpc.stockAlerts.getOosAlertSettings.useQuery(undefined, {
    enabled: open,
  });
  const historyQuery = trpc.stockAlerts.getOosAlertHistory.useQuery(
    { limit: 20 },
    { enabled: open },
  );

  const settings = settingsQuery.data;
  const history = historyQuery.data ?? [];
  // Setup counts come with the settings (BLU-1383). null or undefined (still
  // loading, a failed count, or an older backend) is unknown, never 0. The
  // threshold count is only run while the SAVED mode is 'threshold', so after
  // switching to it the count stays unknown until the save's refetch.
  const keyItemCount = settings?.keyItemCount ?? null;
  const thresholdSkuCount = settings?.thresholdSkuCount ?? null;

  // --- Local form state ---
  const [enabled, setEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [cooldownHours, setCooldownHours] = useState('4');
  const [isDirty, setIsDirty] = useState(false);

  // Sync local state when the saved settings load or change. Keyed on the
  // copied fields only: a refetch that changes just a count must not wipe
  // unsaved edits.
  const savedEnabled = settings?.enabled;
  const savedCooldownHours = settings?.cooldownHours;
  const savedAlertMode = settings?.alertMode;
  useEffect(() => {
    if (savedEnabled === undefined) return;
    setEnabled(savedEnabled);
    setCooldownHours(String(savedCooldownHours));
    setAlertMode(savedAlertMode ?? 'key_items');
    setWebhookUrl('');
    setIsDirty(false);
  }, [savedEnabled, savedCooldownHours, savedAlertMode]);

  // --- Mutations ---
  const utils = trpc.useUtils();

  const updateMutation = trpc.stockAlerts.updateOosAlertSettings.useMutation({
    onSuccess: () => {
      toast.success('Alert settings saved');
      setIsDirty(false);
      setWebhookUrl('');
      utils.stockAlerts.getOosAlertSettings.invalidate();
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to save settings');
    },
  });

  const testMutation = trpc.stockAlerts.testOosAlert.useMutation({
    onSuccess: (result) => {
      if (result.sent) {
        toast.success('Test alert sent to Slack!');
      } else {
        toast.error(result.error ?? 'Failed to send test alert');
      }
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to send test alert');
    },
  });

  const handleSave = () => {
    const payload: { enabled?: boolean; webhookUrl?: string | null; cooldownHours?: number; alertMode?: 'key_items' | 'all_items' | 'threshold' } = {};
    if (settings) {
      if (enabled !== settings.enabled) payload.enabled = enabled;
      if (webhookUrl.trim()) payload.webhookUrl = webhookUrl.trim();
      if (Number(cooldownHours) !== settings.cooldownHours) payload.cooldownHours = Number(cooldownHours);
      if (alertMode !== (settings.alertMode ?? 'key_items')) payload.alertMode = alertMode;
    } else {
      payload.enabled = enabled;
      if (webhookUrl.trim()) payload.webhookUrl = webhookUrl.trim();
      payload.cooldownHours = Number(cooldownHours);
      payload.alertMode = alertMode;
    }
    updateMutation.mutate(payload);
  };

  const markDirty = () => setIsDirty(true);

  // --- Setup progress ---
  const hasWebhook = settings?.hasWebhookConfigured ?? false;
  const isEnabled = settings?.enabled ?? false;
  const step3Done =
    alertMode === 'key_items' ? (keyItemCount ?? 0) > 0
    : alertMode === 'all_items' ? true
    : (thresholdSkuCount ?? 0) > 0;
  const stepsComplete = [hasWebhook, isEnabled, step3Done].filter(Boolean).length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15">
              <BellRing className="h-4 w-4 text-primary" />
            </div>
            <SheetTitle>Out-of-Stock Alerts</SheetTitle>
          </div>
          <SheetDescription>
            {alertMode === 'key_items' && 'Get notified in Slack when starred items go out of stock.'}
            {alertMode === 'all_items' && 'Get notified in Slack when any item goes out of stock.'}
            {alertMode === 'threshold' && 'Get notified in Slack when items drop below their restock threshold.'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Migration banner — per-item alerts replaced by the weekly digest */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex gap-2.5">
            <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900 space-y-1">
              <p className="font-medium">Per-item Slack alerts have been replaced by a weekly digest.</p>
              <p>
                Starred items in the red and yellow stock bands are now sent as one Tuesday
                roll-up instead of a message per SKU. Configure the channel and recipients in{' '}
                <Link
                  to="/notifications"
                  className="font-medium underline hover:no-underline"
                  onClick={() => onOpenChange(false)}
                >
                  Settings → Notifications
                </Link>
                . The settings below no longer trigger Slack messages; recent alerts are
                kept for history.
              </p>
            </div>
          </div>

          {/* Setup Progress */}
          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Setup ({stepsComplete}/3)</p>
              {stepsComplete === 3 && (
                <Badge variant="success" size="sm">Ready</Badge>
              )}
            </div>
            <div className="space-y-2">
              <SetupStep
                done={hasWebhook}
                label="Configure Slack webhook"
              />
              <SetupStep
                done={isEnabled}
                label="Enable alerts"
              />
              {alertMode === 'key_items' && (
                <SetupStep
                  done={step3Done}
                  label={
                    keyItemCount !== null && keyItemCount > 0
                      ? `${keyItemCount} key item${keyItemCount === 1 ? '' : 's'} marked`
                      : 'Mark key items'
                  }
                  hint={
                    keyItemCount === 0 ? (
                      <Link
                        to="/inventory"
                        className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                        onClick={() => onOpenChange(false)}
                      >
                        Go to Inventory <ExternalLink className="h-3 w-3" />
                      </Link>
                    ) : undefined
                  }
                />
              )}
              {alertMode === 'all_items' && (
                <SetupStep
                  done={true}
                  label="Monitoring all SKUs"
                />
              )}
              {alertMode === 'threshold' && (
                <SetupStep
                  done={step3Done}
                  label={
                    thresholdSkuCount !== null && thresholdSkuCount > 0
                      ? `${thresholdSkuCount} SKU${thresholdSkuCount === 1 ? '' : 's'} with thresholds`
                      : 'Set restock thresholds'
                  }
                  hint={
                    thresholdSkuCount === 0 ? (
                      <Link
                        to="/inventory"
                        className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                        onClick={() => onOpenChange(false)}
                      >
                        Go to Inventory <ExternalLink className="h-3 w-3" />
                      </Link>
                    ) : undefined
                  }
                />
              )}
            </div>
          </div>

          <Separator />

          {/* Enable/Disable Switch */}
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="oos-enabled">Enable Alerts</Label>
              <p className="text-xs text-muted-foreground">
                {alertMode === 'key_items' && 'Send Slack notifications when key items hit zero stock.'}
                {alertMode === 'all_items' && 'Send Slack notifications when any SKU hits zero stock.'}
                {alertMode === 'threshold' && 'Send Slack notifications when stock drops to or below restock thresholds.'}
              </p>
            </div>
            <Switch
              id="oos-enabled"
              checked={enabled}
              onCheckedChange={(checked) => {
                setEnabled(checked);
                markDirty();
              }}
            />
          </div>

          {/* Alert Mode */}
          <div className="space-y-2">
            <Label>Alert Mode</Label>
            <Select
              value={alertMode}
              onValueChange={(v: string) => {
                setAlertMode(v as 'key_items' | 'all_items' | 'threshold');
                setIsDirty(true);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="key_items">Key Items Only</SelectItem>
                <SelectItem value="all_items">All Items</SelectItem>
                <SelectItem value="threshold">Items with Threshold</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {alertMode === 'key_items' && 'Only alert for starred (key) items when they hit zero stock.'}
              {alertMode === 'all_items' && 'Alert for any SKU when stock reaches zero.'}
              {alertMode === 'threshold' && 'Alert when stock drops to or below each SKU\'s restock threshold. SKUs without a threshold alert at zero.'}
            </p>
          </div>

          {/* Webhook URL */}
          <div className="space-y-2">
            <Label htmlFor="oos-webhook-url">Slack Webhook URL</Label>
            <Input
              id="oos-webhook-url"
              type="url"
              placeholder={
                hasWebhook
                  ? 'Webhook configured. Enter a new URL to replace it.'
                  : 'https://hooks.slack.com/services/...'
              }
              value={webhookUrl}
              onChange={(e) => {
                setWebhookUrl(e.target.value);
                markDirty();
              }}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Create an incoming webhook in your Slack workspace: Apps &rarr; Incoming Webhooks &rarr; Add to Slack.
            </p>
          </div>

          {/* Cooldown Hours */}
          <div className="space-y-2">
            <Label>Alert Cooldown</Label>
            <div className="flex items-center gap-3">
              <Select
                value={cooldownHours}
                onValueChange={(v) => {
                  setCooldownHours(v);
                  markDirty();
                }}
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COOLDOWN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                between repeat alerts for the same SKU + location
              </span>
            </div>
          </div>

          {/* Save Button */}
          {isDirty && (
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="w-full"
            >
              {updateMutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              )}
              Save Changes
            </Button>
          )}

          {/* Test Alert Button */}
          {hasWebhook && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
            >
              {testMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
              ) : (
                <Send className="h-3.5 w-3.5 mr-2" />
              )}
              Send Test Alert
            </Button>
          )}

          <Separator />

          {/* Recent Alerts */}
          <div className="space-y-3">
            <p className="text-sm font-medium">Recent Alerts</p>

            {!hasWebhook && (
              <p className="text-sm text-muted-foreground italic">
                Connect Slack to receive alerts when {alertMode === 'key_items' ? 'key items go' : alertMode === 'all_items' ? 'any SKU goes' : 'stock drops below thresholds or goes'} out of stock.
              </p>
            )}

            {hasWebhook && alertMode === 'key_items' && keyItemCount === 0 && (
              <p className="text-sm text-muted-foreground italic">
                No key items marked.{' '}
                <Link
                  to="/inventory"
                  className="text-primary hover:underline"
                  onClick={() => onOpenChange(false)}
                >
                  Star items in Inventory
                </Link>{' '}
                to track them.
              </p>
            )}

            {hasWebhook && alertMode === 'threshold' && thresholdSkuCount === 0 && (
              <p className="text-sm text-muted-foreground italic">
                No SKUs have restock thresholds.{' '}
                <Link
                  to="/inventory"
                  className="text-primary hover:underline"
                  onClick={() => onOpenChange(false)}
                >
                  Set them in Inventory
                </Link>.
              </p>
            )}

            {hasWebhook && step3Done && history.length === 0 && (
              <p className="text-sm text-muted-foreground italic">
                Watching for stock changes. Alerts will appear here.
              </p>
            )}

            {history.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start justify-between gap-2 rounded-md border px-3 py-2 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <code className="text-xs font-medium truncate">
                          {entry.sku}
                        </code>
                        <Badge
                          variant={entry.slackSent ? 'success' : 'destructive'}
                          size="sm"
                        >
                          {entry.slackSent ? 'Sent' : 'Failed'}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {entry.locationName ?? 'Unknown location'}
                        {' \u00B7 '}
                        Stock: {entry.stockLevel}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                      {formatDistanceToNow(new Date(entry.alertedAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** Small helper for the setup checklist */
function SetupStep({
  done,
  label,
  hint,
}: {
  done: boolean;
  label: string;
  hint?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      {done ? (
        <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
      ) : (
        <Circle className="h-4 w-4 text-muted-foreground/40 mt-0.5 shrink-0" />
      )}
      <div>
        <span className={`text-sm ${done ? 'text-foreground' : 'text-muted-foreground'}`}>
          {label}
        </span>
        {hint && <div className="mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}
